import { useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Minus, Plus, ShoppingBag, CheckCircle2, ChefHat, Bell } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { API, errMsg, fmt, fileUrl } from "@/lib/api";
import { Logo } from "@/components/Brand";

const STEPS = [["yeni", "Siparişiniz alındı", Bell], ["hazirlaniyor", "Hazırlanıyor", ChefHat], ["teslim", "Masanıza geldi", CheckCircle2]];

function StatusTracker({ orderId }) {
  const [o, setO] = useState(null);
  useEffect(() => {
    const load = () => axios.get(`${API}/public/order-status/${orderId}`).then((r) => setO(r.data)).catch(() => {});
    load();
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [orderId]);
  if (!o) return null;
  const idx = o.status === "iptal" ? -1 : Math.max(0, STEPS.findIndex(([s]) => s === o.status));
  return (
    <div className="rounded-3xl bg-white border p-5" data-testid="qr-order-status">
      <p className="font-display font-bold">Sipariş #{o.no} • {fmt(o.total)}</p>
      {o.status === "iptal" ? <p className="text-red-600 text-sm mt-2">Sipariş iptal edildi.</p> : (
        <div className="mt-4 space-y-3">
          {STEPS.map(([s, l, Icon], i) => (
            <div key={s} className={`flex items-center gap-3 ${i <= idx ? "text-[#0F1F45]" : "text-slate-300"}`}>
              <span className={`h-9 w-9 rounded-full grid place-items-center ${i <= idx ? "bg-[#FF6B0A] text-white" : "bg-slate-100"}`}><Icon className="h-4 w-4" /></span>
              <span className="font-semibold text-sm">{l}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function QrMenu() {
  const { slug } = useParams();
  const [sp] = useSearchParams();
  const tableId = sp.get("masa");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [cat, setCat] = useState("all");
  const [cart, setCart] = useState({});
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [sending, setSending] = useState(false);
  const [orderId, setOrderId] = useState(() => sessionStorage.getItem(`gs_qr_${slug}`));

  useEffect(() => {
    axios.get(`${API}/public/menu/${slug}`, { params: tableId ? { table: tableId } : {} }).then((r) => setData(r.data)).catch((e) => setError(errMsg(e)));
  }, [slug, tableId]);

  const products = useMemo(() => data?.products || [], [data]);
  const shown = cat === "all" ? products : products.filter((p) => p.category_id === cat);
  const items = useMemo(() => Object.entries(cart).filter(([, q]) => q > 0).map(([id, qty]) => ({ p: products.find((x) => x.id === id), qty })).filter((x) => x.p), [cart, products]);
  const total = items.reduce((s, i) => s + i.p.price * i.qty, 0);
  const count = items.reduce((s, i) => s + i.qty, 0);
  const change = (id, d) => setCart((c) => ({ ...c, [id]: Math.max(0, (c[id] || 0) + d) }));

  const send = async () => {
    setSending(true);
    try {
      const { data: o } = await axios.post(`${API}/public/order/${slug}`, {
        table_id: data.table ? data.table.id : null,
        items: items.map((i) => ({ product_id: i.p.id, qty: i.qty })),
        note, customer_name: name, customer_phone: phone, address,
      });
      sessionStorage.setItem(`gs_qr_${slug}`, o.id);
      setOrderId(o.id); setCart({}); setOpen(false); setNote("");
    } catch (e) { setError(errMsg(e)); } finally { setSending(false); }
  };

  if (error && !data) return <div className="min-h-screen grid place-items-center p-6 text-center text-slate-600" data-testid="qr-error">{error}</div>;
  if (!data) return <div className="min-h-screen grid place-items-center text-slate-500">Menü yükleniyor...</div>;

  return (
    <div className="min-h-screen bg-slate-50 pb-28" data-testid="qr-menu-page">
      <header className="bg-[#0F1F45] text-white px-5 pt-8 pb-6 rounded-b-[2rem]">
        <div className="flex items-center gap-3">
          <div className="bg-white rounded-2xl p-1"><Logo className="h-11 w-11" /></div>
          <div><h1 className="font-display text-2xl font-extrabold" data-testid="qr-business-name">{data.business.name}</h1>
            <p className="text-sm text-white/70" data-testid="qr-table-name">{data.table ? data.table.name : "Dijital Menü"}</p></div>
        </div>
      </header>
      <div className="sticky top-0 z-10 bg-slate-50/90 backdrop-blur px-4 py-3 flex gap-2 overflow-x-auto">
        <button onClick={() => setCat("all")} className={`rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap ${cat === "all" ? "bg-[#FF6B0A] text-white" : "bg-white border"}`} data-testid="qr-cat-all">Tümü</button>
        {data.categories.map((c) => <button key={c.id} onClick={() => setCat(c.id)} className={`rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap ${cat === c.id ? "bg-[#FF6B0A] text-white" : "bg-white border"}`} data-testid={`qr-cat-${c.id}`}>{c.name}</button>)}
      </div>
      <div className="px-4 space-y-3 max-w-2xl mx-auto">
        {orderId && <StatusTracker orderId={orderId} />}
        {shown.map((p) => (
          <div key={p.id} className="flex gap-3 rounded-3xl bg-white border p-3" data-testid={`qr-product-${p.id}`}>
            {p.image ? <img src={fileUrl(p.image)} alt={p.name} className="h-24 w-24 rounded-2xl object-cover shrink-0" /> : <div className="h-24 w-24 rounded-2xl bg-slate-100 shrink-0" />}
            <div className="flex-1 min-w-0 flex flex-col">
              <p className="font-semibold">{p.name}</p>
              <p className="text-xs text-slate-500 line-clamp-2">{p.description}</p>
              <div className="mt-auto flex items-center justify-between">
                <span className="font-bold text-[#FF6B0A]">{fmt(p.price)}</span>
                {cart[p.id] ? (
                  <div className="flex items-center gap-2">
                    <Button size="icon" variant="outline" className="h-8 w-8 rounded-full" onClick={() => change(p.id, -1)} data-testid={`qr-minus-${p.id}`}><Minus className="h-3.5 w-3.5" /></Button>
                    <span className="font-bold w-4 text-center">{cart[p.id]}</span>
                    <Button size="icon" className="h-8 w-8 rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]" onClick={() => change(p.id, 1)} data-testid={`qr-plus-${p.id}`}><Plus className="h-3.5 w-3.5" /></Button>
                  </div>
                ) : <Button size="sm" className="rounded-full bg-[#0F1F45]" onClick={() => change(p.id, 1)} data-testid={`qr-add-${p.id}`}>Ekle</Button>}
              </div>
            </div>
          </div>
        ))}
        {!shown.length && <p className="text-center text-slate-500 py-10">Bu kategoride ürün yok.</p>}
        {!data.table && <p className="text-center text-xs text-slate-500 py-4">Paket sipariş için sepetinize ürün ekleyin; adresinizi sonraki adımda gireceksiniz.</p>}
      </div>
      {count > 0 && (
        <div className="fixed bottom-0 inset-x-0 p-4">
          <Button className="w-full max-w-2xl mx-auto flex h-14 rounded-full bg-[#FF6B0A] hover:bg-[#E55A00] text-base gap-2 shadow-xl" onClick={() => setOpen(true)} data-testid="qr-cart-btn">
            <ShoppingBag className="h-5 w-5" /> Sepeti Gör ({count}) • {fmt(total)}
          </Button>
        </div>
      )}
      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="bottom" className="rounded-t-[2rem] max-h-[85vh] overflow-y-auto" data-testid="qr-cart-sheet">
          <SheetHeader><SheetTitle className="font-display text-xl">Sepetiniz{data.table ? ` • ${data.table.name}` : " • Paket Sipariş"}</SheetTitle></SheetHeader>
          <div className="space-y-2 mt-4">
            {items.map(({ p, qty }) => <div key={p.id} className="flex justify-between text-sm"><span>{qty}x {p.name}</span><span className="font-semibold">{fmt(p.price * qty)}</span></div>)}
          </div>
          <Input className="mt-4" placeholder="Adınız (isteğe bağlı)" value={name} onChange={(e) => setName(e.target.value)} data-testid="qr-name-input" />
          {!data.table && (
            <>
              <Input className="mt-2" placeholder="Telefon numaranız *" type="tel" value={phone} onChange={(e) => setPhone(e.target.value)} data-testid="qr-phone-input" />
              <Input className="mt-2" placeholder="Teslimat adresiniz *" value={address} onChange={(e) => setAddress(e.target.value)} data-testid="qr-address-input" />
            </>
          )}
          <Input className="mt-2" placeholder="Sipariş notu" value={note} onChange={(e) => setNote(e.target.value)} data-testid="qr-note-input" />
          {error && <p className="text-sm text-red-600 mt-2">{error}</p>}
          <Button className="w-full mt-4 h-12 rounded-full bg-[#FF6B0A] hover:bg-[#E55A00]" disabled={sending || (!data.table && (phone.trim().length < 7 || !address.trim()))} onClick={send} data-testid="qr-submit-order-btn">Siparişi Gönder • {fmt(total)}</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
